import { normalizeEmail } from '@open-dataroom/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import * as React from 'react';

import { GroupSelect, RoomSelect } from '~/components/admin-entity-select';
import { Button } from '~/components/ui/button';
import { Dialog, DialogPanel } from '~/components/ui/dialog';
import { Input } from '~/components/ui/input';
import { api, ApiError } from '~/lib/api';

type InviteTarget = 'room' | 'group';

export interface InviteRoomViewerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set, the invite is only for this room (room selector hidden). */
  roomId?: string;
  roomName?: string;
  onSent?: () => void;
}

export function InviteRoomViewerModal({
  open,
  onOpenChange,
  roomId: fixedRoomId,
  roomName: fixedRoomName,
  onSent
}: InviteRoomViewerModalProps) {
  const queryClient = useQueryClient();
  const useRoomPicker = !fixedRoomId;
  const [email, setEmail] = React.useState('');
  const [target, setTarget] = React.useState<InviteTarget>('room');
  const [selectedRoomId, setSelectedRoomId] = React.useState('');
  const [selectedGroupId, setSelectedGroupId] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  const { data: roomsData } = useQuery({
    queryKey: ['rooms'],
    queryFn: () =>
      api.get<{ rooms: Array<{ id: string; name: string }> }>('/api/rooms'),
    enabled: open && useRoomPicker
  });
  const rooms = roomsData?.rooms ?? [];

  const effectiveRoomId = fixedRoomId ?? selectedRoomId;
  const effectiveRoomName =
    fixedRoomName ??
    rooms.find((room) => room.id === effectiveRoomId)?.name ??
    'this room';

  const { data: groupsData } = useQuery({
    queryKey: ['groups', effectiveRoomId, 'with-room-access'],
    queryFn: () =>
      api.get<{ groups: Array<{ id: string; name: string }> }>(
        `/api/groups?roomId=${encodeURIComponent(effectiveRoomId)}&hasRoomAccess=true`
      ),
    enabled: open && target === 'group' && !!effectiveRoomId
  });
  const groups = groupsData?.groups ?? [];

  React.useEffect(() => {
    if (open && useRoomPicker && rooms.length > 0 && !selectedRoomId) {
      setSelectedRoomId(rooms[0]!.id);
    }
  }, [open, useRoomPicker, rooms, selectedRoomId]);

  React.useEffect(() => {
    if (target !== 'group') return;
    const first = groups[0]?.id;
    if (first && !groups.some((g) => g.id === selectedGroupId)) {
      setSelectedGroupId(first);
    }
  }, [target, groups, selectedGroupId]);

  React.useEffect(() => {
    if (!open) {
      setEmail('');
      setError(null);
      setTarget('room');
      if (useRoomPicker) {
        setSelectedRoomId('');
        setSelectedGroupId('');
      }
    }
  }, [open, useRoomPicker]);

  const invite = useMutation({
    mutationFn: (body: { email: string; roomId: string; groupId?: string }) =>
      api.post('/api/invites', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invites'] });
      onSent?.();
      onOpenChange(false);
    },
    onError: (e: unknown) => {
      setError(
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : 'Request failed'
      );
    }
  });

  const titleId = React.useId();
  const needsRoom = useRoomPicker;
  const needsGroup = target === 'group';
  const hasRoom = !!effectiveRoomId;
  const hasGroup = !needsGroup || (!!selectedGroupId && groups.length > 0);
  const canSubmit =
    email.trim().length > 0 &&
    hasRoom &&
    hasGroup &&
    !invite.isPending &&
    (needsRoom ? rooms.length > 0 : true);

  const description = (() => {
    if (target === 'group') {
      return `Add them to a group that already has access to ${effectiveRoomName}. When they accept, they join the group and inherit its permissions (including this room).`;
    }
    return `Give them direct viewer access to ${effectiveRoomName} without adding them to a group.`;
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPanel aria-labelledby={titleId}>
        <h2
          id={titleId}
          className="text-lg font-semibold leading-none tracking-tight"
        >
          Invite viewer
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">{description}</p>

        <form
          className="mt-4 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            if (!canSubmit || !effectiveRoomId) return;
            const payload: {
              email: string;
              roomId: string;
              groupId?: string;
            } = {
              email: normalizeEmail(email),
              roomId: effectiveRoomId
            };
            if (target === 'group' && selectedGroupId) {
              payload.groupId = selectedGroupId;
            }
            invite.mutate(payload);
          }}
        >
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium">Access type</span>
            <div className="inline-flex w-fit rounded-md border p-1">
              <Button
                type="button"
                size="sm"
                variant={target === 'room' ? 'secondary' : 'ghost'}
                className="h-8 px-3"
                disabled={invite.isPending}
                onClick={() => setTarget('room')}
              >
                Direct room
              </Button>
              <Button
                type="button"
                size="sm"
                variant={target === 'group' ? 'secondary' : 'ghost'}
                className="h-8 px-3"
                disabled={invite.isPending}
                onClick={() => setTarget('group')}
              >
                Via group
              </Button>
            </div>
          </div>

          {needsRoom ? (
            <div className="space-y-2">
              <label htmlFor="invite-room-pick" className="text-sm font-medium">
                Room for this invite
              </label>
              {rooms.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Create a room from the dashboard before inviting users.
                </p>
              ) : (
                <RoomSelect
                  id="invite-room-pick"
                  value={selectedRoomId}
                  onValueChange={(value) => {
                    setSelectedRoomId(value);
                    setSelectedGroupId('');
                  }}
                  rooms={rooms}
                  placeholder="Choose a room"
                  ariaLabel="Choose room for invite"
                  disabled={invite.isPending}
                />
              )}
            </div>
          ) : null}

          {needsGroup ? (
            <div className="space-y-2">
              <label
                htmlFor="invite-group-pick"
                className="text-sm font-medium"
              >
                Group
              </label>
              {!effectiveRoomId ? (
                <p className="text-sm text-muted-foreground">
                  Choose a room first.
                </p>
              ) : groups.length === 0 ? (
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p>
                    No groups have access to {effectiveRoomName} yet. Create a
                    group, grant it access with Share on that room, then return
                    here.
                  </p>
                  <p>
                    <Link
                      to="/settings/groups"
                      className="font-medium text-primary underline-offset-4 hover:underline"
                      onClick={() => onOpenChange(false)}
                    >
                      Open Settings → Groups
                    </Link>
                  </p>
                </div>
              ) : (
                <GroupSelect
                  id="invite-group-pick"
                  value={selectedGroupId}
                  onValueChange={setSelectedGroupId}
                  groups={groups}
                  placeholder="Choose a group"
                  ariaLabel="Choose group for invite"
                  disabled={invite.isPending}
                />
              )}
            </div>
          ) : null}

          <div className="space-y-2">
            <label htmlFor="invite-room-email" className="text-sm font-medium">
              Email
            </label>
            <Input
              id="invite-room-email"
              type="email"
              autoComplete="email"
              placeholder="viewer@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={invite.isPending}
              autoFocus
            />
          </div>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              disabled={invite.isPending}
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {invite.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending…
                </>
              ) : (
                'Send invite'
              )}
            </Button>
          </div>
        </form>
      </DialogPanel>
    </Dialog>
  );
}
